"use client";

import { useEffect, useState, Suspense } from "react";
import type { BusinessSignup as BusinessSignupBlock } from "@docid/types/src/payload-types";
import type { Locale } from "@/i18n-config";
import type { getDictionary } from "@/get-dictionary";
import { SignupForm } from "@/components/account/signup/form/Signup";
import { InlineText } from "@/components/content/common/InlineText";
import { PrimaryButtonLink } from "@/components/forms/PrimaryButtonLink";
import { Notification } from "@/components/interaction/Notification";
import { LangLink } from "@/components/locales/LangLink";
import { AppleSignin } from "@/components/login/AppleSignin";
import { useClientAuth } from "@/components/providers/client/AuthProvider";
import { resolveBusinessSignupState } from "@/app/actions/content/blocks/businessSignup/actions";

export type BusinessSignupDictionary = Pick<
  Awaited<ReturnType<typeof getDictionary>>,
  | "captchaTerms"
  | "errorPasswordShouldBeMin6CharsLong"
  | "errorUserAlreadyRegistered"
  | "formCountriesOthers"
  | "formCountriesSuggested"
  | "loginOrOther"
  | "loginWithApple"
  | "signupButton"
  | "signupButtonSendToken"
  | "signupButtonValidateToken"
  | "signupByEmailAndPassword"
  | "signupByEmailAndPasswordMobile"
  | "signupByPhone"
  | "signupByPhoneMobile"
  | "signupEmail"
  | "signupEmailPlaceholder"
  | "signupPassword"
  | "signupPasswordPlaceholder"
  | "signupPhone"
  | "signupPhonePlaceholder"
  | "signupPhoneToken"
  | "signupPhoneTokenPlaceholder"
  | "signupTermsWithApple"
  | "validationErrorEmailRequired"
  | "validationErrorInvalidCountry"
  | "validationErrorInvalidLang"
  | "validationErrorInvalidPhone"
  | "validationErrorLengthMin"
  | "validationErrorPasswordDigit"
  | "validationErrorPasswordLowercase"
  | "validationErrorPasswordSymbol"
  | "validationErrorPasswordUppercase"
  | "validationErrorTypeString"
>;

export function BusinessSignupContent({
  block,
  lang,
  dictionary,
}: {
  block: BusinessSignupBlock;
  lang: Locale;
  dictionary: BusinessSignupDictionary;
}) {
  const { isUserReady, user } = useClientAuth();
  const [state, setState] = useState<Awaited<
    ReturnType<typeof resolveBusinessSignupState>
  > | null>(null);
  useEffect(() => {
    if (!isUserReady) return;
    let active = true;
    setState(null);
    resolveBusinessSignupState()
      .then((value) => {
        if (active) setState(value);
      })
      .catch((error) =>
        console.error("Failed to load business signup state", error),
      );
    return () => {
      active = false;
    };
  }, [isUserReady, user?.id]);
  if (state?.kind === "hidden") return null;

  return (
    <section id="business-signup" className="text-center text-white">
      <div className="mx-auto w-full">
        <h2 className="text-3xl font-semibold leading-tight md:text-5xl">
          <InlineText value={block.title} />
        </h2>
        <p className="mt-4 text-lg leading-normal md:text-xl">
          <InlineText value={block.description} />
        </p>

        {!state && (
          <div className="mt-8">
            <PrimaryButtonLink
              lang={lang}
              href="/signup?type=business"
              label={<InlineText value={block.continueButtonLabel} />}
            />
          </div>
        )}
        {state &&
          (state.kind === "member" ? (
            <div className="mt-8 rounded-2xl bg-white px-5 pt-8 pb-8 md:pb-12 text-zinc-700 shadow-2xl shadow-gray-900/10 md:px-8">
              <h2 className="mb-10 pt-6 text-center text-2xl font-bold leading-snug text-zinc-900 md:text-3xl">
                <InlineText value={block.formHeadline} />
              </h2>
              <p className="mb-6 text-lg leading-normal">
                <InlineText value={block.continueDescription} />
              </p>
              <PrimaryButtonLink
                lang={lang}
                href="/signup/business"
                label={<InlineText value={block.continueButtonLabel} />}
              />
            </div>
          ) : (
            <Suspense fallback={null}>
              <div className="mt-8 rounded-2xl bg-white px-5 py-8 text-zinc-700 shadow-2xl shadow-gray-900/10 md:px-8">
                <h2 className="mb-10 pt-6 text-center text-2xl font-bold leading-snug text-zinc-900 md:text-3xl">
                  <InlineText value={block.formHeadline} />
                </h2>
                <div>
                  <Suspense fallback={null}>
                    <Notification
                      className="pb-4"
                      translations={{
                        "User already registered":
                          dictionary.errorUserAlreadyRegistered,
                        "Password should be at least 6 characters":
                          dictionary.errorPasswordShouldBeMin6CharsLong,
                      }}
                    />
                  </Suspense>

                  <div className="flex justify-center">
                    <SignupForm
                      lang={lang}
                      country={state.country}
                      type="business"
                      successPathname="/signup/business"
                      dictionary={{
                        formCountriesSuggested:
                          dictionary.formCountriesSuggested,
                        formCountriesOthers: dictionary.formCountriesOthers,
                        validationErrorTypeString:
                          dictionary.validationErrorTypeString,
                        validationErrorInvalidLang:
                          dictionary.validationErrorInvalidLang,
                        validationErrorInvalidCountry:
                          dictionary.validationErrorInvalidCountry,
                        validationErrorLengthMin:
                          dictionary.validationErrorLengthMin,
                        validationErrorInvalidPhone:
                          dictionary.validationErrorInvalidPhone,
                        validationErrorPhoneTokenRequired:
                          dictionary.signupPhoneToken,
                        validationErrorEmailRequired:
                          dictionary.validationErrorEmailRequired,
                        validationErrorPasswordLowercase:
                          dictionary.validationErrorPasswordLowercase,
                        validationErrorPasswordUppercase:
                          dictionary.validationErrorPasswordUppercase,
                        validationErrorPasswordDigit:
                          dictionary.validationErrorPasswordDigit,
                        validationErrorPasswordSymbol:
                          dictionary.validationErrorPasswordSymbol,
                        signupPhone: dictionary.signupPhone,
                        signupPhoneToken: dictionary.signupPhoneToken,
                        signupPhoneTokenPlaceholder:
                          dictionary.signupPhoneTokenPlaceholder,
                        signupPhonePlaceholder:
                          dictionary.signupPhonePlaceholder,
                        signupEmail: dictionary.signupEmail,
                        signupEmailPlaceholder:
                          dictionary.signupEmailPlaceholder,
                        signupPassword: dictionary.signupPassword,
                        signupPasswordPlaceholder:
                          dictionary.signupPasswordPlaceholder,
                        signupByPhone: dictionary.signupByPhone,
                        signupByPhoneMobile: dictionary.signupByPhoneMobile,
                        signupByEmailAndPassword:
                          dictionary.signupByEmailAndPassword,
                        signupByEmailAndPasswordMobile:
                          dictionary.signupByEmailAndPasswordMobile,
                        signupButton: dictionary.signupButton,
                        signupButtonSendToken: dictionary.signupButtonSendToken,
                        signupButtonValidateToken:
                          dictionary.signupButtonValidateToken,
                      }}
                      light
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <LangLink
                    lang={lang}
                    href="/login?type=business"
                    className="text-lg font-normal leading-tight text-zinc-700 underline hover:text-zinc-900 md:text-xl"
                    scroll
                  >
                    <InlineText value={block.alreadyRegisteredLabel} />
                  </LangLink>
                </div>

                <div className="mt-8 flex flex-row items-center justify-center gap-4 px-5 md:mt-10">
                  <div className="grow border-t border-zinc-200" />
                  <div className="text-lg font-normal text-zinc-500">
                    {dictionary.loginOrOther}
                  </div>
                  <div className="grow border-t border-zinc-200" />
                </div>

                <div className="mt-6 flex flex-col items-center justify-center px-5 md:mt-8">
                  <AppleSignin
                    lang={lang}
                    type="business"
                    dictionary={{ loginWithApple: dictionary.loginWithApple }}
                  />
                </div>

                <p
                  className="mt-6 text-normal leading-normal text-zinc-600 md:mt-8 md:text-lg md:leading-snug"
                  dangerouslySetInnerHTML={{
                    __html: dictionary.signupTermsWithApple,
                  }}
                />
                <p
                  className="mt-6 text-normal leading-normal text-zinc-600 md:mt-8 md:text-lg md:leading-snug"
                  dangerouslySetInnerHTML={{
                    __html: dictionary.captchaTerms
                      .replace(
                        "{privacyUrl}",
                        "https://policies.google.com/privacy",
                      )
                      .replace(
                        "{termsUrl}",
                        "https://policies.google.com/terms",
                      ),
                  }}
                />
              </div>
            </Suspense>
          ))}
      </div>
    </section>
  );
}
